<script setup lang="ts">
import {Dialog, DialogContent, DialogDescription, DialogTitle} from "@/core/components/ui/dialog";
import {ArrowDown, Clock, Gift, Video} from "@lucide/vue";
import {EXPERT_FOLLOW_UP_MEETING_URL} from "@/core/services/SupportService";

const open = defineModel<boolean>("open", {required: true});

const meetingEmbedUrl = `${EXPERT_FOLLOW_UP_MEETING_URL}?embed=true`;

const benefits = [
    {
        icon: "🎯",
        title: "Configuration personnalisée",
        description: "Paramétrage adapté à votre activité et vos besoins spécifiques"
    },
    {
        icon: "🚀",
        title: "Prise en main accélérée",
        description: "Gagnez du temps avec un accompagnement sur-mesure"
    },
    {
        icon: "💡",
        title: "Conseils d'expert",
        description: "Réponses à toutes vos questions par nos spécialistes"
    }
];
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent
      class="flex flex-col gap-0 p-0 w-[85vw] max-w-[950px] max-h-[90vh] overflow-hidden dialog-slide-up"
      @interact-outside="event => event.preventDefault()"
    >
      <div class="hidden">
        <DialogTitle>Rendez-vous expert</DialogTitle>
        <DialogDescription>Prenez rendez-vous avec un expert</DialogDescription>
      </div>
      <div class="relative bg-gradient-to-br from-primary/5 via-primary/10 to-primary/40 px-6 py-8 border-b">
        <div
          class="inline-flex items-center gap-2 bg-primary/10 text-primary px-3 py-1.5 rounded-full text-sm leading-tight font-medium mb-4"
        >
          <Gift class="h-4 w-4" />
          <span>Période d'essai activée</span>
        </div>

        <h2 class="text-3xl font-bold mb-2">
          Démarrez sur de bonnes bases 🎉
        </h2>
        <p>
          Profitez d'un rendez-vous offert avec un expert pour optimiser votre utilisation dès le départ
        </p>
      </div>

      <div class="overflow-y-auto flex-1">
        <div class="px-6 py-6">
          <div class="grid md:grid-cols-3 gap-4 mb-8">
            <div
              v-for="benefit in benefits"
              :key="benefit.title"
              class="bg-secondary/30 rounded-lg p-4 hover:bg-secondary/50 transition-colors cursor-default"
            >
              <div class="text-2xl mb-2">
                {{ benefit.icon }}
              </div>
              <h3 class="font-semibold text-sm mb-1">
                {{ benefit.title }}
              </h3>
              <p class="text-xs leading-relaxed">
                {{ benefit.description }}
              </p>
            </div>
          </div>

          <div class="flex flex-wrap gap-6 mb-6 px-4 py-3 bg-primary/5 rounded-lg border border-primary/10">
            <div class="flex items-center gap-2 text-sm leading-tight">
              <div class="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                <Video class="h-4 w-4 text-primary" />
              </div>
              <span class="font-medium">Visioconférence</span>
            </div>
            <div class="flex items-center gap-2 text-sm leading-tight">
              <div class="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                <Clock class="h-4 w-4 text-primary" />
              </div>
              <span><strong>30 minutes</strong> d'accompagnement</span>
            </div>
            <div class="flex items-center gap-2 text-sm  leading-tight">
              <div class="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                <Gift class="h-4 w-4 text-primary" />
              </div>
              <span><strong>Gratuit</strong> et sans engagement</span>
            </div>
          </div>

          <div class="bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg p-6 mb-6 border border-primary/20">
            <h3 class="font-semibold text-lg mb-2 flex items-center gap-2">
              Réservez votre créneau maintenant
              <ArrowDown class="h-5 w-5 text-primary" />
            </h3>
            <p class="text-sm text-muted-foreground mb-4">
              Choisissez le moment qui vous convient le mieux pour votre rendez-vous d'accompagnement
            </p>
          </div>

          <div class="border rounded-lg overflow-hidden">
            <component
              :is="'script'"
              src="https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js"
              async
              defer
            />
            <div
              class="meetings-iframe-container min-h-[500px]"
              :data-src="meetingEmbedUrl"
            />
          </div>
        </div>
      </div>

      <div class="border-t bg-muted/20 px-6 py-4">
        <p class="text-xs text-center text-muted-foreground">
          💡 <strong>Pas le temps maintenant ?</strong> Nous vous avons envoyé un email avec le lien pour réserver votre
          rendez-vous à votre convenance 😉
        </p>
      </div>
    </DialogContent>
  </Dialog>
</template>

<style scoped>
.meetings-iframe-container {
    @apply rounded-lg overflow-hidden bg-white;
}
</style>
